/**
 * TargetSearchStep — "Search" step of Combine & Filter. Wraps the shared
 * `SearchTypeahead` the same way `NewImportPage.tsx:101-127` does: a thin
 * `search()` callback over `client.listSearches`, mapping its
 * `SearchListResponseDTO` shape to `SearchTypeaheadResult`.
 *
 * `value` is the target the recruiter CONFIRMED, never pre-filled: when every
 * selected source shares one Thrive search, `CombineFilterPage` offers it
 * next to this field ("Use this search") and nothing is checked in Thrive
 * until it is taken (epic 2026-10-01-combine-stability-perf, sub-task 02).
 * The field is REQUIRED before Generate — the page owns that rule (this
 * component only renders the control). "Change" keeps the confirmed target
 * in force until another search is picked (PR #993 E2E S5).
 */
import { useCallback } from "react";

import type { ImportsClient } from "../../../../clients/imports.client";
import {
  SearchTypeahead,
  type SearchTypeaheadResult,
} from "../../new-import/steps/SearchTypeahead";

export interface TargetSearchStepProps {
  client: ImportsClient;
  value: string | null;
  valueName: string | null;
  onChange: (searchId: string | null, searchName: string | null) => void;
}

export function TargetSearchStep({ client, value, valueName, onChange }: TargetSearchStepProps) {
  const search = useCallback(
    async (query: string): Promise<SearchTypeaheadResult> => {
      const res = await (client.listSearches?.(query ? { q: query } : {}) ??
        Promise.resolve({ items: [], total: 0 }));
      return {
        items: (res.items ?? []).map((o) => ({
          id: o.id,
          name: o.name,
          jobTitle: o.job_title ?? null,
          company: o.company ?? null,
          isTest: o.is_test,
        })),
        total: res.total ?? res.items?.length ?? 0,
      };
    },
    [client]
  );

  return (
    <div data-testid="combine-target-search" className="flex flex-col gap-1.5">
      {/* `keepValueWhileChanging`: "Change" opens the picker over the
          confirmed target instead of clearing it, so nothing is re-read (no
          target-less preview, no "no target" rows) until a new search is
          picked — and "Keep …"/Escape backs out with no request at all. */}
      <SearchTypeahead
        value={value}
        valueName={valueName}
        onChange={onChange}
        search={search}
        keepValueWhileChanging
      />
    </div>
  );
}
